import { Input, InputGroup, Select } from '..';

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';

import {
  ADDRESS_FIELDS,
  ADDRESS_STRUCTURE,
  POSTAL_CODE_FORMATS,
  VALIDATION_MESSAGES,
} from '../../../constants/address-structure';
import { countries } from '../../../constants/countries';
import { getFullCountryName } from '../../../helpers/countries';
import DisplayBox from '../../DisplayBox/DisplayBox';
import { GoogleAddressInput } from '../GoogleAddressInput/GoogleAddressInput';
import styles from './Address.module.scss';

export const Address = ({
  name,
  value = {},
  title,
  description,
  tooltip,
  autoComplete,
  defaultValue,
  required = false,
  register,
  setValue,
  control,
  error,
  noErrorMessage = false,
}) => {
  const [errorMessage, setErrorMessage] = useState(error);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [showAddressBox, setShowAddressBox] = useState(true);
  const hasShownAddressBoxRef = useRef(false);

  // Watch the entire address object to get all nested values
  const watchedAddressValue = useWatch({
    control,
    name: name,
  });

  // Watch country value specifically for manual address mode
  const countryValue = useWatch({
    control,
    name: `${name}.country`,
  });

  // Watch manual_address_entry to respect the toggle state
  const manualAddressEntry = useWatch({
    control,
    name: `${name}.manual_address_entry`,
  });

  // Construct the complete address value from watched values or prop
  // The watched value may contain nested fields that the prop doesn't show
  const completeAddressValue = watchedAddressValue || value || {};

  const selectedCountry =
    countryValue || completeAddressValue?.country || getFullCountryName(defaultValue);
  const structure = ADDRESS_STRUCTURE[selectedCountry] || ADDRESS_STRUCTURE.default;

  const googleFieldValidators = {
    required: {
      value: required && !isManualEntry,
      message: 'Address is required',
    },
  };

  useEffect(() => {
    if (!error || Object.keys(error).length === 0) {
      setErrorMessage(null);
      return;
    }

    // Get all errors
    const allErrors = Object.values(error);

    // First check for required errors
    const requiredErrors = allErrors.filter(err => err?.type === 'required');
    if (requiredErrors.length > 0) {
      setErrorMessage(
        requiredErrors.length > 1
          ? 'Please complete all required address fields'
          : requiredErrors[0].message,
      );
      return;
    }

    // If no required errors, check for other error types
    const otherErrors = allErrors.filter(err => err?.message);
    if (otherErrors.length > 0) {
      setErrorMessage(
        otherErrors.length > 1
          ? 'Please fix the errors in the address fields'
          : otherErrors[0].message,
      );
      return;
    }

    setErrorMessage(null);
  }, [error]);

  // Sync isManualEntry with the manual_address_entry field value
  useEffect(() => {
    if (manualAddressEntry !== undefined) {
      setIsManualEntry(manualAddressEntry);
    }
  }, [manualAddressEntry]);

  useEffect(() => {
    // Check if we have any address field values using the complete address value
    const hasValues = ADDRESS_FIELDS.some(field => completeAddressValue[field.fieldName]);

    if (hasValues) {
      // Only set manual entry state if manual_address_entry hasn't been explicitly set
      // This prevents overriding user's manual toggle
      if (manualAddressEntry === undefined || manualAddressEntry === null) {
        // If there's a google_address value, keep manual entry off
        // Otherwise, set manual entry to true
        setIsManualEntry(!completeAddressValue.google_address);
        // Also set the form field value
        try {
          setValue(`${name}.manual_address_entry`, !completeAddressValue.google_address, {
            shouldValidate: false,
            shouldDirty: true,
          });
        } catch (error) {
          console.warn('Could not set manual_address_entry:', error);
        }
      }
      // Only show address box initially when values first appear, not on every change
      // Don't reset it if user has already dismissed it (showAddressBox is false)
      if (!hasShownAddressBoxRef.current) {
        setShowAddressBox(true);
        hasShownAddressBoxRef.current = true;
      }

      // If there's a country value, ensure it's in the correct format
      const currentCountry = countryValue || completeAddressValue?.country;
      if (currentCountry) {
        const formattedCountry = getFullCountryName(currentCountry);
        if (formattedCountry !== currentCountry) {
          try {
            setValue(`${name}.country`, formattedCountry, {
              shouldValidate: true,
              shouldDirty: true,
            });
          } catch (error) {
            console.warn('Could not set country in useEffect:', error);
          }
        }
      }
      // If no country is set but we have a default value, set it
      else if (defaultValue) {
        const formattedCountry = getFullCountryName(defaultValue);
        try {
          setValue(`${name}.country`, formattedCountry, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } catch (error) {
          console.warn('Could not set default country in useEffect:', error);
        }
      }
    } else {
      // If there are no values, reset the ref so DisplayBox can show again when values are entered
      hasShownAddressBoxRef.current = false;
      setShowAddressBox(false);
    }
  }, [completeAddressValue, countryValue, setValue, name, defaultValue, manualAddressEntry]);

  const getFieldValidators = fieldName => {
    const fieldValidators = {};

    // Add required validation if field is in structure and not suburb
    if (structure[fieldName] && fieldName !== 'suburb' && required) {
      fieldValidators.required = {
        value: true,
        message: `${structure[fieldName]} is required`,
      };
    }

    // Add postal code validation if applicable
    if (fieldName === 'postal_code' && structure.postal_code) {
      const postalFormat = POSTAL_CODE_FORMATS[selectedCountry] || POSTAL_CODE_FORMATS.default;
      fieldValidators.pattern = {
        value: new RegExp(postalFormat),
        message:
          VALIDATION_MESSAGES.postal_code[selectedCountry] ||
          VALIDATION_MESSAGES.postal_code.default,
      };
    }

    return fieldValidators;
  };

  const handleManualEntry = e => {
    const isManual = e.target.checked;

    // Set the manual_address_entry field value
    try {
      setValue(`${name}.manual_address_entry`, isManual, {
        shouldValidate: false,
        shouldDirty: true,
      });
    } catch (error) {
      console.warn('Could not set manual_address_entry:', error);
    }

    if (isManual) {
      // When switching to manual, always hide the DisplayBox and show fields directly
      setShowAddressBox(false);
      // Set the ref to true so DisplayBox won't show when clearAddress runs
      hasShownAddressBoxRef.current = true;

      // Clear google_address when switching to manual
      try {
        setValue(`${name}.google_address`, '', {
          shouldValidate: false,
          shouldDirty: true,
        });
      } catch (error) {
        console.warn('Could not clear google_address:', error);
      }
      // Don't reset the display box ref when clearing from manual toggle
      clearAddress(false);
    } else {
      // When switching back to Google address, reset the ref
      hasShownAddressBoxRef.current = false;
    }

    setIsManualEntry(isManual);
    setErrorMessage(null);
  };

  const handleAddressSelect = placeDetails => {
    const findComponent = componentTypes => {
      // Special handling for street_address to combine multiple components
      if (componentTypes.includes('street_number')) {
        const subpremise =
          placeDetails?.addressComponents?.find(component => component.types.includes('subpremise'))
            ?.longText || '';

        const streetNumber =
          placeDetails?.addressComponents?.find(component =>
            component.types.includes('street_number'),
          )?.longText || '';

        const route =
          placeDetails?.addressComponents?.find(component => component.types.includes('route'))
            ?.longText || '';

        // Construct street_address as: "1/1066 Frankton Road"
        const baseStreet = [streetNumber, route].filter(Boolean).join(' ');
        const fullStreet = subpremise ? `${subpremise}/${baseStreet}` : baseStreet;

        return fullStreet;
      }

      // Handle other fields normally
      for (const type of componentTypes) {
        const component = placeDetails?.addressComponents?.find(component =>
          component.types.includes(type),
        );
        if (component?.longText) {
          return component.longText;
        }
      }
      return '';
    };

    // Build a complete new address object to replace the entire nested object
    // This avoids the "object is not extensible" error
    const newAddressObject = { ...completeAddressValue };

    // Clear all existing address fields first
    ADDRESS_FIELDS.forEach(field => {
      newAddressObject[field.fieldName] = '';
    });

    // Update each address field using the googleComponents mapping from ADDRESS_FIELDS
    ADDRESS_FIELDS.forEach(field => {
      if (field.googleComponents) {
        const fieldValue = findComponent(field.googleComponents);
        newAddressObject[field.fieldName] = fieldValue;
      } else {
        newAddressObject[field.fieldName] = '';
      }
    });

    // Handle country separately since it needs special formatting
    const countryAlpha2 =
      placeDetails?.addressComponents?.find(c => c.types.includes('country'))?.shortText ||
      findComponent(['country']); // fallback if no shortText

    const formattedCountry = getFullCountryName(countryAlpha2);
    if (formattedCountry) {
      newAddressObject.country = formattedCountry;
    } else if (defaultValue) {
      // Set default country if no country found
      newAddressObject.country = getFullCountryName(defaultValue);
    }

    // Set google_address and manual_address_entry
    newAddressObject.google_address = placeDetails?.formattedAddress || '';
    newAddressObject.manual_address_entry = false;

    // Replace the entire nested object at once
    try {
      setValue(name, newAddressObject, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } catch (error) {
      console.error('Could not set address object:', error);
      // Fallback: try setting fields individually (might still fail but worth trying)
      ADDRESS_FIELDS.forEach(field => {
        try {
          const fieldValue = field.googleComponents ? findComponent(field.googleComponents) : '';
          setValue(`${name}.${field.fieldName}`, fieldValue, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } catch (fieldError) {
          console.warn(`Could not set ${field.fieldName}:`, fieldError);
        }
      });
    }

    setIsManualEntry(false);
  };

  const clearAddress = (resetDisplayBoxRef = true) => {
    // Reset the address box ref so it can show again when new values are entered
    // Unless we're switching to manual mode, in which case we want to keep it hidden
    if (resetDisplayBoxRef) {
      hasShownAddressBoxRef.current = false;
    }

    // Build a complete new address object with cleared fields
    // This avoids the "object is not extensible" error
    const clearedAddressObject = { ...completeAddressValue };

    // Clear all address fields
    ADDRESS_FIELDS.forEach(field => {
      clearedAddressObject[field.fieldName] = '';
    });

    // Set country to default
    const formattedCountry = getFullCountryName(defaultValue);
    if (formattedCountry) {
      clearedAddressObject.country = formattedCountry;
    }

    // Replace the entire nested object at once
    try {
      setValue(name, clearedAddressObject, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } catch (error) {
      console.warn('Could not clear address object, trying individual fields:', error);
      // Fallback: try setting fields individually
      ADDRESS_FIELDS.forEach(field => {
        try {
          setValue(`${name}.${field.fieldName}`, '', {
            shouldValidate: true,
            shouldDirty: true,
          });
        } catch (fieldError) {
          console.warn(`Could not clear ${field.fieldName}:`, fieldError);
        }
      });
      if (formattedCountry) {
        try {
          setValue(`${name}.country`, formattedCountry, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } catch (countryError) {
          console.warn('Could not set country:', countryError);
        }
      }
    }
  };

  const hasAddressValues = () => {
    return isManualEntry || completeAddressValue.google_address;
  };

  const getFormattedAddress = () => {
    const addressParts = ADDRESS_FIELDS.map(field => completeAddressValue[field.fieldName]).filter(
      Boolean,
    );
    const country = countries.find(c => c.value === completeAddressValue.country)?.name;
    if (country) addressParts.push(country);
    return addressParts;
  };

  return (
    <InputGroup title={title} description={description} tooltip={tooltip} error={errorMessage}>
      <div
        className={cn(styles.addressContainer, {
          [styles.addressContainerError]: errorMessage,
        })}>
        <GoogleAddressInput
          name={`${name}.google_address`}
          parentName={name}
          register={register}
          setValue={setValue}
          onManualEntry={handleManualEntry}
          onAddressSelect={handleAddressSelect}
          country={defaultValue}
          hasManualEntry={isManualEntry}
          validators={googleFieldValidators}
        />

        {isManualEntry && (
          <>
            {ADDRESS_FIELDS.some(field => completeAddressValue[field.fieldName]) &&
            showAddressBox ? (
              <DisplayBox show={true} cancelable onCancel={() => setShowAddressBox(false)}>
                <div className="d-flex flex-column u-align-items-center">
                  {getFormattedAddress().map((part, index) => (
                    <span key={index} className="secondary-text t-body">
                      {part}
                    </span>
                  ))}
                </div>
              </DisplayBox>
            ) : (
              <div className={styles.manualFields}>
                {ADDRESS_FIELDS.map(field => {
                  const isFieldInStructure =
                    structure[field.fieldName] && structure[field.fieldName] !== null;

                  return isFieldInStructure ? (
                    <Input
                      key={field.fieldName}
                      name={`${name}.${field.fieldName}`}
                      type="text"
                      autoComplete={autoComplete === 'current' ? field.autocomplete : undefined}
                      placeholder={structure[field.fieldName]}
                      maxLength={field.maxLength}
                      validators={isManualEntry ? getFieldValidators(field.fieldName) : {}}
                      register={register}
                      noErrorMessage={true}
                      className="u-margin-bottom--small"
                    />
                  ) : null;
                })}
                <Select
                  name={`${name}.country`}
                  value={countryValue || selectedCountry}
                  placeholder="Select Country..."
                  defaultValue={getFullCountryName(defaultValue)}
                  useSearch
                  validators={required && isManualEntry ? { required: 'Country is required' } : {}}
                  register={register}
                  error={error?.country?.message}
                  noErrorMessage={true}>
                  {countries.map(country => (
                    <Select.Item key={country.value} value={country.value}>
                      {country.name}
                    </Select.Item>
                  ))}
                </Select>
              </div>
            )}
          </>
        )}
        <DisplayBox show={!hasAddressValues()}>
          <div className="d-flex flex-column u-align-items-center">
            <span className="primary-text title-5 text-center">No address selected</span>
            <span className="secondary-text t-body text-center">
              Search for, or enter an address manually
            </span>
          </div>
        </DisplayBox>
      </div>
      {errorMessage && !noErrorMessage && <span className={styles.error}>{errorMessage}</span>}
    </InputGroup>
  );
};

Address.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
  tooltip: PropTypes.string,
  autoComplete: PropTypes.string,
  defaultValue: PropTypes.string,
  required: PropTypes.bool,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  noErrorMessage: PropTypes.bool,
};
